import { z } from "zod";
import {
  BridgeError,
  type BookingBridge,
  type BookingCommand,
  type CommandResult,
  type HotelBootstrap,
} from "./contract";
import {
  bookingProjectionSchema,
  bookingStatusReportSchema,
  checkoutSessionSchema,
  hotelBootstrapSchema,
} from "./schemas";
import { consumeContinuationToken } from "../bootstrap";

const sessionSchema = z.object({
  token: z.string().min(1),
  expiresAt: z.string().min(1),
  flowId: z.string().uuid().nullable().optional(),
});

const commandEnvelopeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ok"), projection: bookingProjectionSchema }),
  z.object({
    kind: z.literal("revision_conflict"),
    code: z.enum(["REVISION_CONFLICT", "QUOTE_EXPIRED"]),
    current: bookingProjectionSchema,
  }),
]);

const apiErrorSchema = z.object({
  code: z.string().optional(),
  message: z.string().optional(),
  reason: z.string().optional(),
  retryable: z.boolean().optional(),
});

function normalizedBase(raw: string): string {
  const value = raw.trim().replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(value)) {
    throw new BridgeError("BRIDGE_NOT_WIRED", "Die Booking-API-Adresse ist ungültig.");
  }
  return value;
}

function bridgeCode(status: number, code?: string): ConstructorParameters<typeof BridgeError>[0] {
  if (status === 429) return "RATE_LIMITED";
  if (status === 404 || code === "FLOW_NOT_FOUND") return "FLOW_NOT_FOUND";
  if (status === 401 || code === "SESSION_REJECTED") return "SESSION_REJECTED";
  if (status === 403 || code === "TENANT_OR_ORIGIN_MISMATCH") return "TENANT_OR_ORIGIN_MISMATCH";
  if (status === 422 || code === "BOOKING_REQUIREMENT_UNSATISFIED") {
    return "BOOKING_REQUIREMENT_UNSATISFIED";
  }
  return "UNAVAILABLE";
}

async function apiError(response: Response): Promise<BridgeError> {
  let body: z.infer<typeof apiErrorSchema> = {};
  try {
    body = apiErrorSchema.parse(await response.json());
  } catch {
    // A proxy may replace an API error with HTML. Do not surface that content to the guest.
  }
  return new BridgeError(
    bridgeCode(response.status, body.code),
    body.message ?? body.reason ?? "Der Buchungsdienst ist gerade nicht verfügbar.",
    body.retryable ?? (response.status >= 500 || response.status === 429),
  );
}

export function createHttpBridge(apiBaseRaw: string): BookingBridge {
  const apiBase = normalizedBase(apiBaseRaw);
  let sessionToken: string | null = null;
  let resumedFlowId: string | null = null;
  const continuationToken = consumeContinuationToken();
  let bootstrapPromise: Promise<HotelBootstrap> | null = null;
  const checkoutIdempotencyKeys = new Map<string, string>();

  async function json<T>(path: string, schema: z.ZodType<T>, init: RequestInit = {}): Promise<T> {
    if (!sessionToken) throw new BridgeError("SESSION_REJECTED", "Die Buchungssitzung ist nicht aktiv.");
    const response = await fetch(`${apiBase}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${sessionToken}`,
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
    if (!response.ok) throw await apiError(response);
    try {
      return schema.parse(await response.json());
    } catch {
      throw new BridgeError("UNAVAILABLE", "Der Buchungsdienst hat eine ungültige Antwort geliefert.");
    }
  }

  async function command(
    path: string,
    revision: number,
    idempotencyKey: string,
    method: "POST" | "PATCH",
    body?: BookingCommand,
  ): Promise<CommandResult> {
    return json(path, commandEnvelopeSchema, {
      method,
      headers: {
        "Idempotency-Key": idempotencyKey,
        "If-Match": String(revision),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    }) as Promise<CommandResult>;
  }

  return {
    async bootstrap(publicKey, launchOrigin) {
      if (bootstrapPromise) return bootstrapPromise;
      bootstrapPromise = (async () => {
        const key = publicKey.trim();
        if (!key && !continuationToken) {
          throw new BridgeError("SESSION_REJECTED", "Für dieses Hotel fehlt der Buchungsschlüssel.");
        }
        const provenLaunchOrigin = launchOrigin ?? (typeof window === "undefined" ? "http://localhost" : window.location.origin);
        const response = await fetch(`${apiBase}/api/v1/public/booking-widget/session`, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify(continuationToken
            ? { continuationToken }
            : { publicKey: key, launchOrigin: provenLaunchOrigin }),
        });
        if (!response.ok) throw await apiError(response);
        const session = sessionSchema.parse(await response.json());
        sessionToken = session.token;
        resumedFlowId = session.flowId ?? null;
        return json("/api/v1/public/booking-widget/bootstrap", hotelBootstrapSchema);
      })();
      return bootstrapPromise;
    },

    createFlow(input) {
      if (resumedFlowId) return json(`/api/v1/public/booking-flows/${encodeURIComponent(resumedFlowId)}`, bookingProjectionSchema);
      return json("/api/v1/public/booking-flows", bookingProjectionSchema, {
        method: "POST",
        body: JSON.stringify(input),
      });
    },

    getFlow(flowId) {
      return json(`/api/v1/public/booking-flows/${encodeURIComponent(flowId)}`, bookingProjectionSchema);
    },

    applyCommand(flowId, revision, bookingCommand, idempotencyKey) {
      return command(
        `/api/v1/public/booking-flows/${encodeURIComponent(flowId)}`,
        revision,
        idempotencyKey,
        "PATCH",
        bookingCommand,
      );
    },

    requestQuote(flowId, revision) {
      return command(
        `/api/v1/public/booking-flows/${encodeURIComponent(flowId)}/quote`,
        revision,
        crypto.randomUUID(),
        "POST",
      );
    },

    createCheckoutSession(flowId, quoteId) {
      const mutationKey = `${flowId}:${quoteId}`;
      const idempotencyKey = checkoutIdempotencyKeys.get(mutationKey) ?? crypto.randomUUID();
      checkoutIdempotencyKeys.set(mutationKey, idempotencyKey);
      return json(
        `/api/v1/public/booking-flows/${encodeURIComponent(flowId)}/checkout-session`,
        checkoutSessionSchema,
        {
          method: "POST",
          headers: { "Idempotency-Key": idempotencyKey },
          body: JSON.stringify({ quoteId }),
        },
      );
    },

    getStatus(flowId) {
      return json(
        `/api/v1/public/booking-flows/${encodeURIComponent(flowId)}/status`,
        bookingStatusReportSchema,
      );
    },
  };
}
